/**
 * Numier Online activity tracker.
 *
 * Records what the user does so the API can compute active users and report usage
 * (tables online_events / online_sessions, see OnlineActivityRepository in numier-api):
 *
 * - "view":   every screen the user enters (router.afterEach).
 * - "query":  a burst of data requests made from a screen. A report usually fires
 *             several requests per "Consultar"; they are grouped into ONE event when no
 *             request is pending for QUERY_QUIET_MS. durationMs = burst wall time.
 * - "export": any request whose endpoint looks like an export / PDF / Excel.
 * - "action": data changes (POST/PUT/DELETE that are not reads). Params are NOT sent.
 * - "logout": explicit logout from the menus.
 *
 * Login is recorded by the API itself. Events are queued and sent in batches every
 * FLUSH_MS and when the tab is hidden, with sendBeacon / fetch keepalive as text/plain
 * (no CORS preflight). The JWT goes in the body. Tracking never throws and never
 * blocks the app: any failure just drops the batch.
 */

const FLUSH_MS = 15000;
const QUERY_QUIET_MS = 1500;
const MAX_QUEUE = 200;
const MAX_BATCH = 50;

// Endpoints that are app plumbing (profile, permissions, business selectors...), not
// something the user asked for. Matched against the path relative to the API URL.
const IGNORED_PREFIXES = [
  "general/",
  "home/",
  "novedades/",
  "activity/",
  "login",
  "terms",
  "termsJWT",
  "updateTerms",
];

const EXPORT_RE = /export|pdf|excel|xlsx?|csv|download|descarga/i;
const READ_RE = /^(get|consult|check|list|search|buscar|obtener|ver|informe)/i;
const SECRET_KEY_RE = /jwt|pass|token|secret|authorization|email|phone|telefono|dni|nif/i;

let store = null;
let queue = [];
let current = null; // { screen, route, module }
let burst = null;   // current "query" burst
let flushTimer = null;

function now() {
  return Date.now();
}

function jwt() {
  return store && store.getters ? store.getters.jwt : "";
}

function apiUrl() {
  return store && store.getters ? store.getters.apiUrl || "" : "";
}

function idBusiness() {
  return store && store.state ? Number(store.state.xidBusiness) || 0 : 0;
}

function push(event) {
  if (!jwt()) return;
  queue.push(Object.assign({ at: now(), idBusiness: idBusiness() }, current || {}, event));
  if (queue.length > MAX_QUEUE) queue = queue.slice(queue.length - MAX_QUEUE);
}

// ------------------------------------------------------------------------------------
// Sending
// ------------------------------------------------------------------------------------

function send(events, token, useBeacon) {
  const url = apiUrl() + "activity/track";
  const body = JSON.stringify({ jwt: token, events: events });

  try {
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
      if (navigator.sendBeacon(url, blob)) return;
    }
    if (window.fetch) {
      window.fetch(url, {
        method: "POST",
        body: body,
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        keepalive: true,
        credentials: "omit",
      }).catch(function () {});
    }
  } catch (e) {
    // Tracking must never break the app
  }
}

export function flush(useBeacon) {
  const token = jwt();
  if (!token || !queue.length || !apiUrl()) return;

  while (queue.length) {
    send(queue.splice(0, MAX_BATCH), token, useBeacon);
  }
}

// ------------------------------------------------------------------------------------
// Screens
// ------------------------------------------------------------------------------------

function screenFromRoute(to) {
  const matched = to.matched && to.matched.length ? to.matched[to.matched.length - 1] : null;
  const route = matched && matched.path ? matched.path : to.path;
  const screen = to.name || route;
  const module = (route || "").split("/").filter(Boolean)[0] || "home";
  return { screen: String(screen).slice(0, 80), route: String(route).slice(0, 160), module: module.slice(0, 40) };
}

export function trackView(to, from) {
  try {
    if (!to || (from && from.path === to.path)) return;
    closeBurst();
    current = screenFromRoute(to);
    push({ type: "view" });
  } catch (e) {}
}

export function trackLogout() {
  try {
    closeBurst();
    push({ type: "logout" });
    flush(true);
  } catch (e) {}
}

// ------------------------------------------------------------------------------------
// API requests (axios interceptors)
// ------------------------------------------------------------------------------------

function endpointOf(config) {
  const base = apiUrl();
  let url = config.url || "";
  if (base && url.indexOf(base) === 0) {
    url = url.slice(base.length);
  } else if (/^https?:\/\//i.test(url)) {
    return null; // third-party call, not our API
  }
  return url.split("?")[0].replace(/^\/+/, "");
}

function isIgnored(endpoint) {
  for (let i = 0; i < IGNORED_PREFIXES.length; i++) {
    if (endpoint.indexOf(IGNORED_PREFIXES[i]) === 0) return true;
  }
  return false;
}

function classify(config, endpoint) {
  const parts = endpoint.split("/").filter(Boolean);
  const last = parts[parts.length - 1] || "";
  // Everything but the first segment (the API group), so "exportX/{id}" still counts
  const action = parts.slice(1).join("/") || last;
  const method = (config.method || "get").toLowerCase();
  if (EXPORT_RE.test(action) || config.responseType === "blob" || config.responseType === "arraybuffer") return "export";
  if (method === "get" || READ_RE.test(parts[1] || last)) return "query";
  return "action";
}

function sanitize(data, depth) {
  const out = {};
  Object.keys(data).forEach(function (key) {
    if (SECRET_KEY_RE.test(key)) return;
    const value = data[key];
    if (value === null || ["string", "number", "boolean"].indexOf(typeof value) !== -1) {
      out[key] = typeof value === "string" ? value.slice(0, 200) : value;
    } else if (Array.isArray(value)) {
      out[key] = value.slice(0, 50).filter(function (v) {
        return v === null || ["string", "number", "boolean"].indexOf(typeof v) !== -1;
      });
    } else if (value && typeof value === "object" && depth < 1 && !(value instanceof Blob)) {
      out[key] = sanitize(value, depth + 1);
    }
  });
  return out;
}

function filtersOf(config) {
  try {
    let data = config.params || {};
    if (config.data) {
      let body = config.data;
      if (typeof body === "string") {
        try { body = JSON.parse(body); } catch (e) { body = null; }
      }
      if (body && typeof body === "object" && !(body instanceof FormData)) {
        data = Object.assign({}, data, body);
      }
    }
    const clean = sanitize(data, 0);
    return JSON.stringify(clean).length <= 2000 ? clean : null;
  } catch (e) {
    return null;
  }
}

function closeBurst() {
  if (!burst) return;
  clearTimeout(burst.timer);
  const b = burst;
  burst = null;
  queue.push(Object.assign({}, b.base, {
    type: "query",
    durationMs: Math.max(0, (b.lastEnd || now()) - b.start),
    result: b.errors ? 1 : 0,
    params: { endpoints: b.endpoints.slice(0, 20), filters: b.filters },
  }));
}

function onRequest(config) {
  try {
    if (!jwt() || !current) return config;
    const endpoint = endpointOf(config);
    if (!endpoint || isIgnored(endpoint)) return config;

    const kind = classify(config, endpoint);
    config.__activity = { kind: kind, endpoint: endpoint, start: now() };

    if (kind === "query") {
      if (!burst) {
        burst = {
          base: Object.assign({ at: now(), idBusiness: idBusiness() }, current),
          start: now(), lastEnd: 0, pending: 0, errors: 0, endpoints: [], filters: filtersOf(config), timer: null,
        };
      }
      clearTimeout(burst.timer);
      burst.pending++;
      if (burst.endpoints.indexOf(endpoint) === -1) burst.endpoints.push(endpoint);
      config.__activity.burst = burst;
    }
  } catch (e) {}
  return config;
}

function onResponse(config, ok) {
  try {
    const a = config && config.__activity;
    if (!a) return;

    if (a.kind === "query") {
      const b = a.burst;
      if (!b || b !== burst) return; // burst already closed (screen change)
      b.pending = Math.max(0, b.pending - 1);
      b.lastEnd = now();
      if (!ok) b.errors++;
      if (b.pending === 0) {
        b.timer = setTimeout(closeBurst, QUERY_QUIET_MS);
      }
      return;
    }

    push({
      type: a.kind,
      durationMs: now() - a.start,
      result: ok ? 0 : 1,
      // Data changes may carry customer data: only the endpoint is kept
      params: a.kind === "export" ? { endpoint: a.endpoint, filters: filtersOf(config) } : { endpoint: a.endpoint },
    });
  } catch (e) {}
}

// ------------------------------------------------------------------------------------
// Install
// ------------------------------------------------------------------------------------

export function installActivityTracker(options) {
  store = options.store;
  const router = options.router;
  const axios = options.axios;

  router.afterEach(function (to, from) {
    trackView(to, from);
  });

  axios.interceptors.request.use(onRequest);
  axios.interceptors.response.use(
    function (res) {
      // The API answers most errors with HTTP 200 and { response: false }
      const ok = !(res && res.data && res.data.response === false);
      onResponse(res && res.config, ok);
      return res;
    },
    function (err) {
      onResponse(err && err.config, false);
      return Promise.reject(err);
    }
  );

  flushTimer = setInterval(function () { flush(false); }, FLUSH_MS);

  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "hidden") {
      closeBurst();
      flush(true);
    }
  });
  window.addEventListener("pagehide", function () {
    closeBurst();
    flush(true);
  });
}



// WEBPACK FOOTER //
// ./src/activity/tracker.js